import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import VehiclePerformanceReportTable from "./vehiclePerformanceReportTable";
import { vehiclePerformanceEndpoint } from "config";
import { AccountsContext } from "context/AccountsContext";


function VehiclePerformanceReport() {
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date()); // ✅ single date only

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Columns
  const columns = [
    { Header: "Vehicle Number", accessor: "thingID", width: "15%" },
    { Header: "Over Speeding Score(%)", accessor: "overSpeedingPercentage" },
    { Header: "Idling Score(%)", accessor: "idlingPercentage" },
    { Header: "Harsh Acceleration Score(%)", accessor: "haScore" },
    { Header: "Harsh Braking Score(%)", accessor: "hbScore" },
    { Header: "Harsh Corner Score(%)", accessor: "hcScore" },
    { Header: "Average Score(%)", accessor: "averageScore" },
  ];

  const formatDate = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const fetchReport = async () => {
    if (!vehicleId) {
      setErrorMessage("Please select a vehicle");
      openErrorSB();
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");

    try {
      const data = {
        date: formatDate(date),
        zone: "Asia/Kolkata",
        things: vehicleId,
        thingIds: [],
      };

      const config = {
        method: "post",
        url:vehiclePerformanceEndpoint, // ✅ update endpoint
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);
      setReportData(response.data);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading vehicle performance report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };
  const fetchFullReport = async () => {
    if (!vehicleId) {
      setErrorMessage("Please select a vehicle");
      openErrorSB();
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");

    try {
      const data = {
        date: formatDate(date),
        zone: "Asia/Kolkata",
        things: "",
        thingIds: [],
      };

      const config = {
        method: "post",
        url:vehiclePerformanceEndpoint, // ✅ update endpoint
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);
      setReportData(response.data);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading vehicle performance report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };
  const downloadCSV = () => {
    if (!reportData || reportData.length === 0) {
      setErrorMessage("No data to download");
      openErrorSB();
      return;
    }

    const headers = columns.map((col) => col.Header).join(",");
    const rows = reportData
      .map((row) =>
        columns.map((col) => row[col.accessor] ?? "").join(",")
      )
      .join("\n");

    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "vehicle_performance_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const clearFilters = () => {
    setVehicleId("");
    setReportData([]);
    setErrorMessage("");
    setDate(new Date());
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>
                    
                    {/* Vehicle Single-Select */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={things || []}
                        getOptionLabel={(option) => option.thingId}
                        value={things.find((t) => t.thingId === vehicleId) || null}
                        onChange={(event, newValue) => {
                          setVehicleId(newValue ? newValue.thingId : "");
                        }}
                        renderOption={(props, option) => (
                          <li {...props} style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            <span>{option.nickName}</span>
                            <span style={{ color: "#666" }}>{option.thingId}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select Vehicle" fullWidth size="small" />
                        )}
                      />
                    </Grid>

                    {/* Date Picker */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Select Date"
                        value={date}
                        onChange={(newValue) => setDate(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="gradient" color="info" size="small" onClick={fetchReport} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="gradient" color="info" size="small" onClick={fetchFullReport} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : "All"}
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="outlined" color="error" size="small" onClick={clearFilters} disabled={loading}>
                        CLEAR
                      </MDButton>
                    </Grid>
                    
                    <Grid item xs={4} sm={2} md={1}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={downloadCSV} disabled={loading} />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {/* Error Alert */}
              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              {/* Loader or Table */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <CircularProgress />
                ) : (
                  <VehiclePerformanceReportTable
                    table={{ columns, rows: reportData }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar color="success" icon="check" title="Vehicle Performance Report" content="Data Loaded Successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite />
      <MDSnackbar color="error" icon="warning" title="Vehicle Performance Report" content="Failed to load report" open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite />
    </DashboardLayout>
  );
}

export default VehiclePerformanceReport;



